import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringify } from 'qs';

import configs from '@webtrader/core/configs';
import { EProjectPlatform } from '@webtrader/core/configs/types';
import { IApiRequest, IApiRequestWithPayload } from '@webtrader/core/request/api';
import { defaultErrorHandler } from '@webtrader/core/request/handler';
import { configService } from '@webtrader/core/services/config';
import { exportService } from '@webtrader/core/services/export';
import { maintenanceService } from '@webtrader/core/services/maintenance';
import storage, { EStorageKey } from '@webtrader/core/storage';

const isNative = configs.platform === EProjectPlatform.NATIVE;

const domainUrl = configs.baseURL;
const axiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json'
    // SK: Comment to codes for cross origin
    // 'X-XSS-Protection': '0',
    // 'Referrer-Policy': 'no-referrer',
    // 'X-Permitted-Cross-Domain-Policies': 'none'
  },
  paramsSerializer: (params) => stringify(params, { arrayFormat: 'comma' })
});

axiosInstance.interceptors.request.use((configs) => {
  const url = configs.url || '';
  const token = storage.getItem<string>(EStorageKey.TOKEN);
  const language = storage.getItem(EStorageKey.LANGUAGE);

  const axiosBaseConfigs: AxiosRequestConfig = {
    ...configs,
    baseURL: configService.getCurrentApiDomain()
  };

  if (token) {
    if (!['/api/auth/v1/company/login'].includes(url)) {
      axiosBaseConfigs.headers = {
        ...axiosBaseConfigs.headers,
        authorization: `Bearer ${token}`
      };
    } else if (['/api/support/v1/ticket'].includes(url)) {
      axiosBaseConfigs.headers = {
        ...axiosBaseConfigs.headers,
        authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data'
      };
    }
  }

  if (isNative) {
    if (!axiosBaseConfigs.headers) {
      axiosBaseConfigs.headers = {};
    }
    axiosBaseConfigs.headers['cookie'] = `lang=${language}`;
  }

  return axiosBaseConfigs;
});

axiosInstance.interceptors.response.use(
  (response) => {
    maintenanceService.checkForMaintenance(response);

    exportService.checkForDownload(response);

    return response.data;
  },
  (error) => {
    const { config } = error;

    /**
     * JW: 27-Jan-2025 - Removed the fallback domain feature due to network issues; the application now relies solely on geolocation-based IP detection.
     */
    // Check if it's a network or DNS failure, switch to fallback URL if primary URL fails
    // const handleFallbackDomain = () => {
    //   if (!configs.apiDomainFallback) {
    //     // eslint-disable-next-line no-console
    //     console.debug('⚠️ No fallback URL configured');
    //     return null;
    //   }

    //   if (config.baseURL === configs.apiDomainFallback) {
    //     // eslint-disable-next-line no-console
    //     console.debug('⚠️ Already using fallback URL, stopping fallback switch loop');
    //     return null;
    //   }

    //   // Possible error when the website is block by GFW
    //   const gfwCodes = ['ECONNRESET', 'ETIMEDOUT', 'ECONNABORTED', 'ENOTFOUND', 'ECONNREFUSED'];

    //   const isNetworkError =
    //     gfwCodes.includes(error.code) ||
    //     error.message.includes('timeout') ||
    //     error.response?.status === 404 ||
    //     error.response?.status === 502;

    //   if (!isNetworkError) {
    //     // eslint-disable-next-line no-console
    //     console.debug('⚠️ Not a network error, error code:', error.code, 'status:', error.response?.status);
    //     return null;
    //   }

    //   configService.switchToFallbackDomain();
    //   config.baseURL = configService.getCurrentApiDomain();
    //   // eslint-disable-next-line no-console
    //   console.debug('🔄 Switching to fallback URL:', configs.apiDomainFallback);
    //   return axiosInstance(config);
    // };

    // const fallbackResult = handleFallbackDomain();
    // if (fallbackResult) {
    //   return fallbackResult;
    // }

    if (!config?.retry) {
      return defaultErrorHandler(error);
    }
    config.retry = config.retry - 1;
    const delayRetryRequest = new Promise<void>((resolve) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.debug('🚧 Retrying request: ', config.url);
        resolve();
      }, config.retryDelay || 1000);
    });
    return delayRetryRequest.then(() => axiosInstance(config));
  }
);

axiosInstance.defaults.withCredentials = true;

class ApiRequest {
  static get: IApiRequest = axiosInstance.get;
  static head: IApiRequest = axiosInstance.head;
  static delete: IApiRequest = axiosInstance.delete;
  static put: IApiRequestWithPayload = axiosInstance.put;
  static post: IApiRequestWithPayload = axiosInstance.post;
  static patch: IApiRequestWithPayload = axiosInstance.patch;
}

export default ApiRequest;

export { axios, axiosInstance, ApiRequest, domainUrl };

export type { AxiosError, AxiosRequestConfig, AxiosResponse };
